import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DECODER_FAILURE,
  VIDEO_FAILURE,
  activeDeviceId,
  cameraConstraints,
  cameraFailure,
  listCameras,
  openCameraStream,
  parseScannedQr,
  stopStream,
} from '../utils/qrScanner';

const SCAN_INTERVAL_MS = 250;
const SLOW_HINT_MS = 15000;
const DECODE_MAX_SIDE = 720;
const ACCENT = '#E0577A';

// Estilo inline: a área da câmera precisa de dimensões mesmo se o CSS do sx
// ainda não tiver sido injetado (mesma solução do PresenceMap).
const stageStyle = (height) => ({
  position: 'relative',
  width: '100%',
  height,
  minHeight: 280,
  borderRadius: 12,
  overflow: 'hidden',
  backgroundColor: '#000',
  border: '1px solid rgba(255,255,255,0.12)',
  boxSizing: 'border-box',
});

const videoStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  backgroundColor: '#000',
};

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 16,
  textAlign: 'center',
  color: '#fff',
  boxSizing: 'border-box',
};

const cornerBase = { position: 'absolute', width: 28, height: 28, borderColor: ACCENT, borderStyle: 'solid' };
const corners = [
  { top: -2, left: -2, borderWidth: '4px 0 0 4px', borderTopLeftRadius: 10 },
  { top: -2, right: -2, borderWidth: '4px 4px 0 0', borderTopRightRadius: 10 },
  { bottom: -2, left: -2, borderWidth: '0 0 4px 4px', borderBottomLeftRadius: 10 },
  { bottom: -2, right: -2, borderWidth: '0 4px 4px 0', borderBottomRightRadius: 10 },
];

function ScanFrame({ size }) {
  return (
    <div style={{ ...overlayStyle, pointerEvents: 'none' }} data-testid="qr-frame">
      <div
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: 12,
          boxShadow: '0 0 0 9999px rgba(6,11,24,0.5)',
        }}
      >
        {corners.map((corner, index) => (
          <span key={index} style={{ ...cornerBase, ...corner }} />
        ))}
      </div>
      <p
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          margin: 0,
          padding: '6px 10px',
          borderRadius: 8,
          background: 'rgba(6,11,24,0.72)',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        Aponte a câmera para o QR Code da atividade
      </p>
    </div>
  );
}

// Os quadros são analisados só em memória e descartados; nada é gravado ou enviado.
async function createDecoder() {
  if (typeof window.BarcodeDetector === 'function') {
    try {
      const formats = await window.BarcodeDetector.getSupportedFormats?.();
      if (!formats || formats.includes('qr_code')) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        return async (video) => (await detector.detect(video))[0]?.rawValue || '';
      }
    } catch {
      // Sem suporte real a QR: usa o decodificador em JavaScript.
    }
  }
  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas indisponível.');
  return async (video) => {
    const { videoWidth, videoHeight } = video;
    if (!videoWidth || !videoHeight) return '';
    const scale = Math.min(1, DECODE_MAX_SIDE / Math.max(videoWidth, videoHeight));
    canvas.width = Math.round(videoWidth * scale);
    canvas.height = Math.round(videoHeight * scale);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(image.data, image.width, image.height, { inversionAttempts: 'attemptBoth' })?.data || '';
  };
}

function ScannerStage({ height, frameSize, deviceId, onDetected, onCameras, onRetry }) {
  const videoRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const onCamerasRef = useRef(onCameras);
  const resumeRef = useRef(null);
  const [status, setStatus] = useState('starting');
  const [failure, setFailure] = useState('');
  const [hint, setHint] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onCamerasRef.current = onCameras;
  }, [onDetected, onCameras]);

  useEffect(() => {
    const video = videoRef.current;
    let cancelled = false;
    let stream = null;
    let timer = null;
    let hintTimer = null;
    let readFailures = 0;
    let lastInvalid = '';

    const release = () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(hintTimer);
      stopStream(stream);
      stream = null;
      if (video) {
        video.pause?.();
        video.srcObject = null;
      }
    };

    const fail = (result) => {
      if (cancelled) return;
      release();
      setStatus(result.state);
      setFailure(result.message);
    };

    const scan = async () => {
      let decode;
      try {
        decode = await createDecoder();
      } catch {
        fail(DECODER_FAILURE);
        return;
      }
      if (cancelled) return;
      setStatus('active');
      hintTimer = setTimeout(() => {
        if (!cancelled) setHint('Nenhum QR identificado ainda. Centralize o código na moldura, aproxime o celular e evite reflexos. Se preferir, feche e digite o PIN.');
      }, SLOW_HINT_MS);

      const tick = async () => {
        if (cancelled) return;
        try {
          if (video.readyState >= 2) {
            const value = await decode(video);
            if (cancelled) return;
            readFailures = 0;
            if (value && parseScannedQr(value).kind !== 'invalid') {
              release();
              onDetectedRef.current(value);
              return;
            }
            if (value && value !== lastInvalid) {
              lastInvalid = value;
              setNotice('Este QR não é um código de presença do PontoNow. Aponte para o QR Code exibido pela organização.');
            }
          }
        } catch {
          readFailures += 1;
          if (readFailures >= 8 && !cancelled) {
            setHint('Erro ao ler a imagem da câmera. Tente de novo ou digite o PIN.');
          }
        }
        if (!cancelled) timer = setTimeout(tick, SCAN_INTERVAL_MS);
      };
      tick();
    };

    const playAndScan = async () => {
      try {
        await video.play();
      } catch (error) {
        if (cancelled) return;
        if (error?.name === 'NotAllowedError') {
          // Safari em modo de economia bloqueia o autoplay: o toque do usuário libera.
          resumeRef.current = async () => {
            try {
              await video.play();
            } catch {
              fail(VIDEO_FAILURE);
              return;
            }
            if (!cancelled) scan();
          };
          setStatus('tap');
          return;
        }
        fail(VIDEO_FAILURE);
        return;
      }
      if (!cancelled) scan();
    };

    const start = async () => {
      if (window.isSecureContext === false) return fail(cameraFailure(null, { secureContext: false }));
      if (!navigator.mediaDevices?.getUserMedia) return fail(cameraFailure(null, { supported: false }));
      try {
        const opened = await openCameraStream(navigator.mediaDevices, cameraConstraints(deviceId));
        if (cancelled) {
          stopStream(opened);
          return undefined;
        }
        stream = opened;
      } catch (error) {
        return fail(cameraFailure(error));
      }
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.srcObject = stream;
      listCameras(navigator.mediaDevices).then((cameras) => {
        if (!cancelled) onCamerasRef.current?.(cameras, activeDeviceId(stream));
      });
      await playAndScan();
      return undefined;
    };

    start();
    return () => {
      resumeRef.current = null;
      release();
    };
  }, [deviceId]);

  const resume = () => {
    setStatus('starting');
    resumeRef.current?.();
  };

  const failed = !['starting', 'active', 'tap'].includes(status);
  const retryable = !['insecure', 'unsupported'].includes(status);

  return (
    <>
      <div style={stageStyle(height)}>
        <video
          ref={videoRef}
          style={{ ...videoStyle, opacity: status === 'active' ? 1 : 0 }}
          playsInline
          autoPlay
          muted
          aria-label="Pré-visualização da câmera"
          data-testid="qr-video"
        />
        {status === 'active' && <ScanFrame size={frameSize} />}
        {status === 'starting' && (
          <div style={overlayStyle} role="status">
            <CircularProgress sx={{ color: ACCENT }} aria-label="Abrindo a câmera" />
            <Typography variant="body2">Abrindo a câmera… Se o navegador pedir, toque em “Permitir”.</Typography>
          </div>
        )}
        {status === 'tap' && (
          <div style={overlayStyle}>
            <Typography variant="body2">O navegador pediu um toque para exibir a câmera.</Typography>
            <Button variant="contained" onClick={resume}>Iniciar câmera</Button>
          </div>
        )}
        {failed && (
          <div style={overlayStyle}>
            <Alert severity={status === 'denied' ? 'warning' : 'error'} sx={{ textAlign: 'left' }} role="alert">
              {failure}
            </Alert>
            {retryable && (
              <Button variant="outlined" onClick={onRetry}>Tentar novamente</Button>
            )}
          </div>
        )}
      </div>
      <Box sx={{ mt: 1.5 }} aria-live="polite">
        {status === 'active' && (
          <Typography variant="body2" color="text.secondary">
            Escanear não registra presença: depois da leitura você confere os dados e confirma a entrada ou a saída.
          </Typography>
        )}
        {notice && status === 'active' && (
          <Alert severity="warning" sx={{ mt: 1 }}>{notice}</Alert>
        )}
        {hint && status === 'active' && (
          <Alert severity="info" sx={{ mt: 1 }}>{hint}</Alert>
        )}
      </Box>
    </>
  );
}

export default function QrScannerDialog({ open, onClose, onDetected }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const [attempt, setAttempt] = useState(0);
  const [deviceId, setDeviceId] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (open) return;
    setCameras([]);
    setActiveId('');
    setDeviceId('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeWhenHidden = () => {
      if (document.visibilityState === 'hidden') onClose();
    };
    document.addEventListener('visibilitychange', closeWhenHidden);
    window.addEventListener('pagehide', onClose);
    return () => {
      document.removeEventListener('visibilitychange', closeWhenHidden);
      window.removeEventListener('pagehide', onClose);
    };
  }, [open, onClose]);

  const handleCameras = useCallback((list, current) => {
    setCameras(list);
    setActiveId(current);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={compact}
      fullWidth
      maxWidth="sm"
      aria-labelledby="qr-scanner-title"
      PaperProps={compact ? { sx: { borderRadius: 0, border: 0 } } : undefined}
    >
      <DialogTitle id="qr-scanner-title">Ler QR Code</DialogTitle>
      <DialogContent sx={{ px: compact ? 2 : 3 }}>
        {open && (
          <ScannerStage
            key={`${attempt}-${deviceId}`}
            height={compact ? 'min(62vh, 520px)' : 420}
            frameSize={compact ? 'min(62vw, 260px)' : 260}
            deviceId={deviceId}
            onDetected={onDetected}
            onCameras={handleCameras}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        )}
        {open && cameras.length > 1 ? (
          <TextField
            select
            fullWidth
            size="small"
            label="Câmera"
            value={cameras.some((camera) => camera.id === (deviceId || activeId)) ? (deviceId || activeId) : ''}
            onChange={(event) => setDeviceId(event.target.value)}
            sx={{ mt: 2 }}
          >
            {cameras.map((camera) => (
              <MenuItem key={camera.id} value={camera.id}>{camera.label}</MenuItem>
            ))}
          </TextField>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: compact ? 2 : 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} fullWidth={compact}>Fechar e digitar o PIN</Button>
      </DialogActions>
    </Dialog>
  );
}
